/**
 * A page's drafted fixes, regrouped for the bar both screens that show it draw (the kind walk's leaves column and the
 * rule screen's rail): the mechanical ones counted by rule, the surfaced ones keyed for `POST /drafts`. Derived from
 * the read every time, never stored.
 */
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { adminClient } from './admin';
import type { BookName } from './page';
import { messageOf } from './refusal';
import { throwOnServerError } from './typed';
import type { components } from './types/api';

export type LeafDrafts = components['schemas']['LeafDrafts'];
export type MechanicalDraft = components['schemas']['MechanicalDraft'];
export type SurfacedDraftRow = components['schemas']['SurfacedDraft'];

/** The drafts of a page as the bar shows them: the mechanical ones counted by rule, the surfaced ones keyed for the post. */
export interface DraftBar {
  readonly mechanical: number;
  readonly rules: readonly { readonly rule: string; readonly count: number }[];
  readonly surfaced: readonly { readonly key: string; readonly word: string; readonly nearest: readonly string[] }[];
  readonly total: number;
}

function isMechanical(draft: MechanicalDraft | SurfacedDraftRow): draft is MechanicalDraft {
  return draft.kind === 'mechanical';
}

/** Group the page's drafts (derived, never stored): rules by name with their counts, surfaced words with their post keys. */
export function draftBar(leaves: readonly LeafDrafts[]): DraftBar {
  const counts = new Map<string, number>();
  const surfaced: { key: string; word: string; nearest: readonly string[] }[] = [];
  for (const leaf of leaves) {
    for (const draft of leaf.drafts) {
      if (isMechanical(draft)) counts.set(draft.rule, (counts.get(draft.rule) ?? 0) + 1);
      else surfaced.push({ key: `surfaced:${leaf.element_id}:${draft.start}`, word: draft.word, nearest: draft.nearest });
    }
  }
  const rules = [...counts].map(([rule, count]) => ({ rule, count }));
  const mechanical = rules.reduce((sum, row) => sum + row.count, 0);
  return { mechanical, rules, surfaced, total: mechanical + surfaced.length };
}

/** The bar's rule line: "rule \u00d7n \u00b7 rule \u00d7n". */
export function rulesLine(rules: DraftBar['rules']): string {
  return rules.map((row) => `${row.rule} \u00d7${row.count}`).join(' \u00b7 ');
}

/**
 * OK all on a page's drafts: one fix per draft, the word chosen for each surfaced one. The screen names the reads the
 * act changed (`invalidates`), each re-read when it lands; `file` resolves to the sentence of a failure, or to nothing.
 */
export function useOkAll(book: BookName, page: number, invalidates: readonly QueryKey[]) {
  const queryClient = useQueryClient();
  const post = useMutation({
    mutationFn: async (chosen: Record<string, string>) => {
      const response = await adminClient.POST('/api/review/drafts', { body: { book, page, chosen } });
      throwOnServerError(response);
      return response.data;
    },
    onSuccess: () => {
      for (const queryKey of invalidates) void queryClient.invalidateQueries({ queryKey });
    },
  });
  return {
    isPending: post.isPending,
    file: async (chosen: Record<string, string>): Promise<string | undefined> => {
      try {
        await post.mutateAsync(chosen);
        return undefined;
      } catch (error) {
        return `The drafted fixes were not filed: ${messageOf(error)}`;
      }
    },
  };
}
